/**
 * Tiny API client. The access token lives in memory only (never localStorage,
 * to limit XSS blast radius); the refresh token is an httpOnly cookie the
 * browser sends to /api/auth/refresh automatically. On a 401 we transparently
 * refresh once and retry.
 */

let accessToken: string | null = null;
let refreshing: Promise<boolean> | null = null;

export function setAccessToken(token: string | null): void {
  accessToken = token;
}
export function getAccessToken(): string | null {
  return accessToken;
}

async function doRefresh(): Promise<boolean> {
  if (!refreshing) {
    refreshing = fetch('/api/auth/refresh', { method: 'POST', credentials: 'include' })
      .then(async (res) => {
        if (!res.ok) return false;
        const data = (await res.json()) as { accessToken: string };
        accessToken = data.accessToken;
        return true;
      })
      .catch(() => false)
      .finally(() => {
        refreshing = null;
      });
  }
  return refreshing;
}

export interface ApiError {
  status: number;
  error: string;
  issues?: unknown;
  /** Seconds to wait before retrying, when the server returns a 429 lockout. */
  retryAfter?: number;
}

export async function api<T = unknown>(
  path: string,
  options: RequestInit & { retry?: boolean } = {},
): Promise<T> {
  const { retry = true, ...init } = options;
  const headers = new Headers(init.headers);
  headers.set('Content-Type', 'application/json');
  if (accessToken) headers.set('Authorization', `Bearer ${accessToken}`);
  // A non-GET request always declares application/json above, even for
  // body-less calls like "POST /x/resend" — Fastify's default JSON parser
  // rejects an empty body under that content-type (400 FST_ERR_CTP_EMPTY_JSON_BODY).
  // Default to '{}' so every such call site doesn't have to remember to.
  const method = (init.method ?? 'GET').toUpperCase();
  const body = init.body ?? (method === 'GET' || method === 'HEAD' ? undefined : '{}');

  const res = await fetch(path, { ...init, headers, body, credentials: 'include' });

  if (res.status === 401 && retry) {
    const ok = await doRefresh();
    if (ok) return api<T>(path, { ...options, retry: false });
  }

  if (!res.ok) {
    let body: { error?: string; issues?: unknown; retryAfter?: number } = {};
    try {
      body = await res.json();
    } catch {
      /* ignore */
    }
    const err: ApiError = {
      status: res.status,
      error: body.error ?? 'request_failed',
      issues: body.issues,
      retryAfter: body.retryAfter,
    };
    throw err;
  }

  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}
